import { type TextFieldProps } from "@mui/material"
import Box from "@mui/material/Box"
import InputLabel from "@mui/material/InputLabel"
import Stack from "@mui/material/Stack"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import React, { useId, useMemo, useState } from "react"
import type { Control, FieldPath, FieldValues } from "react-hook-form"
import { Controller } from "react-hook-form"

import { INPUT_LABEL_BLUR, PRIMARY } from "@constants/colors"
import { inputSizePaddingMap } from "@constants/sizes"

import styles from "./styles"

export type TextInputProps<
  T extends FieldValues,
  K = unknown
> = TextFieldProps & {
  name: FieldPath<T>
  control: Control<T>
  placeholder: string
  label?: string
  TitleRight?: React.ReactNode
  TextLeft?: React.ReactNode
  TextRight?: React.ReactNode
  selectOptions?: {
    label: string
    value: string | number | boolean
    color?: string
    icon?: string
    group?: string
  }[]
  renderTags?: (value: K[]) => React.ReactNode
  getOptionLabel?: (option: K) => string
  tagType?: "default" | "chip"
  groupBy?: boolean
  disableHelper?: boolean
  hasSoundOption?: boolean
  loading?: boolean
  onInputChange?: (
    event: React.SyntheticEvent,
    value: string,
    reason: "input" | "reset" | "clear"
  ) => void
}

export default function PrimaryInput<T extends FieldValues, K = unknown>(
  props: TextInputProps<T, K>
) {
  const [isFocus, setIsFocus] = useState(false)
  const componentId = useId()

  const TitleRightMemo = useMemo(() => {
    if (!props?.TitleRight) {
      return undefined
    }

    return props.TitleRight
  }, [props])

  const TextLeftMemo = useMemo(() => {
    if (!props?.TextLeft) {
      return undefined
    }

    return props.TextLeft
  }, [props])

  const TextRightMemo = useMemo(() => {
    if (!props?.TextRight) {
      return undefined
    }

    return props.TextRight
  }, [props])

  function handleOnFocus(
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>
  ) {
    setIsFocus(true)
    props?.onFocus && props?.onFocus(e)
  }

  function handleOnBlur(
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>
  ) {
    setIsFocus(false)
    props?.onBlur && props?.onBlur(e)
  }

  return (
    <Box sx={styles.container}>
      <Controller<FieldValues>
        name={props.name}
        control={props.control as unknown as Control<FieldValues>}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <>
            <Stack
              justifyContent={"space-between"}
              direction={"row"}
              spacing={2}
            >
              <InputLabel
                error={!!error}
                htmlFor={componentId}
                sx={{
                  ...styles.labelContainer,
                  color: isFocus ? PRIMARY : INPUT_LABEL_BLUR,
                  alignItems: "center"
                }}
                required={props?.required}
              >
                <Typography fontSize={props?.size === "small" ? 14 : undefined}>
                  {props.label}
                </Typography>
              </InputLabel>
              {TitleRightMemo}
            </Stack>
            <TextField
              helperText={!props?.disableHelper && error ? error.message : null}
              inputRef={props?.inputRef}
              error={!!error}
              onChange={onChange}
              value={value as FieldValues}
              fullWidth
              variant="outlined"
              onBlur={handleOnBlur}
              onFocus={handleOnFocus}
              id={componentId}
              {...props}
              InputProps={{
                startAdornment: TextLeftMemo,
                endAdornment: TextRightMemo
              }}
              sx={{
                " input": {
                  padding: inputSizePaddingMap.get(props?.size),
                  fontSize: props?.size === "small" ? 14 : undefined
                },
                ".MuiFormHelperText-root": {
                  marginLeft: 0
                },
                ".MuiSelect-select": {
                  padding: inputSizePaddingMap.get(props?.size)
                },
                ".MuiSelect-select caption": {
                  color: "text.disabled"
                },
                ...props?.sx
              }}
              label={undefined}
            />
          </>
        )}
      />
    </Box>
  )
}
